import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  JobItem,
  JobCreateRequest,
  JobUpdateRequest,
} from "../../interfaces/Job";
import { ProjectsResponse } from "./types";
import * as projectService from "../../services/projectService";

interface ProjectState {
  items: JobItem[];
  currentItem: JobItem | null;
  loading: boolean;
  error: string | null;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

const initialState: ProjectState = {
  items: [],
  currentItem: null,
  loading: false,
  error: null,
  pageNumber: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 0,
};

// Async thunk to fetch projects from API
export const fetchProjects = createAsyncThunk(
  "projects/fetchProjects",
  async (params: {
    pageNumber?: number;
    pageSize?: number;
    clientId?: string;
    status?: number;
    search?: string;
  } = {}) => {
    const {
      pageNumber = 1,
      pageSize = 20,
      clientId,
      status,
      search,
    } = params;
    const response: ProjectsResponse = await projectService.getProjects(
      pageNumber,
      pageSize,
      clientId,
      status,
      search
    );
    return response;
  }
);

// Async thunk to fetch job by ID (formerly project)
export const fetchProjectById = createAsyncThunk(
  "projects/fetchProjectById",
  async (id: string) => {
    const response: JobItem = await projectService.getProjectById(id);
    return response;
  }
);

// Async thunk to fetch projects by client ID
export const fetchProjectsByClient = createAsyncThunk(
  "projects/fetchProjectsByClient",
  async (params: {
    clientId: string;
    pageNumber?: number;
    pageSize?: number;
  }) => {
    const { clientId, pageNumber = 1, pageSize = 20 } = params;
    const response: ProjectsResponse = await projectService.getProjectsByClient(
      clientId,
      pageNumber,
      pageSize
    );
    return response;
  }
);

// Async thunk to create a new job (formerly project)
export const createProject = createAsyncThunk(
  "projects/createProject",
  async (payload: JobCreateRequest) => {
    const response: JobItem = await projectService.createProject(payload);
    return response;
  }
);

// Async thunk to update an existing job (formerly project)
export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async ({ id, payload }: { id: string; payload: JobUpdateRequest }) => {
    const response: JobItem = await projectService.updateProject(id, payload);
    return response;
  }
);

// Async thunk to fetch job by quote ID (formerly project)
export const fetchProjectByQuoteId = createAsyncThunk(
  "projects/fetchProjectByQuoteId",
  async (quoteId: string) => {
    const response: JobItem = await projectService.getProjectByQuoteId(quoteId);
    return response;
  }
);

const projectSlice = createSlice({
  name: "Projects",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearCurrentItem(state) {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        // Map JobItem[] to include projectNumber for backward compatibility
        state.items = action.payload.items.map((job: JobItem) => ({
          ...job,
          projectNumber: job.jobNumber,
        } as JobItem & { projectNumber: string }));
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch jobs";
      })
      // Fetch project by ID
      .addCase(fetchProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.loading = false;
        // Map jobNumber to projectNumber for backward compatibility
        state.currentItem = {
          ...action.payload,
          projectNumber: action.payload.jobNumber,
        } as JobItem & { projectNumber: string };
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch job";
      })
      // Fetch projects by client
      .addCase(fetchProjectsByClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectsByClient.fulfilled, (state, action) => {
        state.loading = false;
        // Map JobItem[] to include projectNumber for backward compatibility
        state.items = action.payload.items.map((job: JobItem) => ({
          ...job,
          projectNumber: job.jobNumber,
        } as JobItem & { projectNumber: string }));
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchProjectsByClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch jobs by client";
      })
      // Create job (formerly project)
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        // Map jobNumber to projectNumber for backward compatibility
        const jobWithProjectNumber = {
          ...action.payload,
          projectNumber: action.payload.jobNumber,
        } as JobItem & { projectNumber: string };
        state.currentItem = jobWithProjectNumber;
        // Add to items list if not already present
        const existingIndex = state.items.findIndex(
          (item) => item.id === action.payload.id
        );
        if (existingIndex === -1) {
          state.items.unshift(jobWithProjectNumber);
        } else {
          state.items[existingIndex] = jobWithProjectNumber;
        }
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create job";
      })
      // Update job (formerly project)
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
        // Update item in list if it exists
        const index = state.items.findIndex(
          (item) => item.id === action.payload.id
        );
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update job";
      })
      // Fetch job by quote ID (formerly project)
      .addCase(fetchProjectByQuoteId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectByQuoteId.fulfilled, (state, action) => {
        state.loading = false;
        // Map jobNumber to projectNumber for backward compatibility
        state.currentItem = {
          ...action.payload,
          projectNumber: action.payload.jobNumber,
        } as JobItem & { projectNumber: string };
      })
      .addCase(fetchProjectByQuoteId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch job by quote ID";
      });
  },
});

export const { clearError, clearCurrentItem } = projectSlice.actions;
export default projectSlice.reducer;

// Selectors
export const selectProjectList = (state: any) => state.Projects?.items || [];
export const selectCurrentProject = (state: any) => state.Projects?.currentItem || null;
export const selectProjectLoading = (state: any) => state.Projects?.loading || false;
export const selectProjectError = (state: any) => state.Projects?.error || null;
export const selectProjectPagination = (state: any) => ({
  pageNumber: state.Projects?.pageNumber || 1,
  pageSize: state.Projects?.pageSize || 20,
  totalCount: state.Projects?.totalCount || 0,
  totalPages: state.Projects?.totalPages || 0,
});
